import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, clearToken, getToken } from "@/lib/api";
import { LogOut, Calendar, Inbox, TrendingUp, RefreshCw } from "lucide-react";
import Logo from "@/components/site/Logo";

const STATUSES = ["new", "contacted", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [b, c, s] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
        api.get("/admin/stats"),
      ]);
      setBookings(b.data.bookings || []);
      setContacts(c.data.contacts || []);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) { clearToken(); nav("/admin/login"); return; }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(id, status) {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Updated.");
    } catch (err) {
      toast.error("Update failed");
    }
  }

  function logout() {
    clearToken();
    nav("/admin/login");
  }

  return (
    <div className="min-h-screen bg-[#F5F4EF]" data-testid="admin-dashboard">
      {/* Sidebar layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] min-h-screen">
        <aside className="border-r border-[#1E1E1E]/10 bg-white p-6 lg:p-8">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={48} />
            <span className="text-[10px] uppercase tracking-[0.24em] text-[#A67C00] font-semibold">Admin</span>
          </Link>
          <nav className="mt-10 space-y-1">
            <SideBtn active={tab === "bookings"} onClick={() => setTab("bookings")} icon={<Calendar size={16} />} label="Bookings" testid="side-bookings" />
            <SideBtn active={tab === "contacts"} onClick={() => setTab("contacts")} icon={<Inbox size={16} />} label="Inquiries" testid="side-contacts" />
            <SideBtn active={tab === "stats"} onClick={() => setTab("stats")} icon={<TrendingUp size={16} />} label="Overview" testid="side-stats" />
          </nav>
          <button onClick={logout} data-testid="admin-logout" className="mt-10 inline-flex items-center gap-2 text-sm text-[#1E1E1E]/70 hover:text-[#A67C00]">
            <LogOut size={14} /> Sign out
          </button>
        </aside>

        <main className="p-6 lg:p-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-[#8A8A8A]">Dashboard</div>
              <h1 className="mt-2 font-display text-3xl md:text-4xl tracking-tight">{tab === "bookings" ? "Assessment Bookings" : tab === "contacts" ? "Contact Inquiries" : "Overview"}</h1>
            </div>
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 text-sm border border-[#1E1E1E]/20 hover:border-[#A67C00] hover:text-[#A67C00] px-4 py-2 transition-colors">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {tab === "stats" && stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard label="Bookings — total" value={stats.bookings_total} />
              <StatCard label="Bookings — new" value={stats.bookings_new} gold />
              <StatCard label="Bookings — confirmed" value={stats.bookings_confirmed} />
              <StatCard label="Contact inquiries" value={stats.contacts_total} />
            </div>
          )}

          {tab === "bookings" && (
            <div className="bg-white border border-[#1E1E1E]/10 overflow-hidden" data-testid="bookings-table">
              <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#1E1E1E]/10 text-[10px] uppercase tracking-[0.24em] text-[#8A8A8A]">
                <div className="col-span-3">Client</div>
                <div className="col-span-2">Service</div>
                <div className="col-span-2">Studio</div>
                <div className="col-span-2">Received</div>
                <div className="col-span-3">Status</div>
              </div>
              {loading ? (
                <div className="p-8 text-sm text-[#8A8A8A]">Loading…</div>
              ) : bookings.length === 0 ? (
                <div className="p-8 text-sm text-[#8A8A8A]">No bookings yet.</div>
              ) : (
                bookings.map((b) => (
                  <div key={b.id} className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[#1E1E1E]/5 items-start" data-testid={`booking-row-${b.id}`}>
                    <div className="col-span-3">
                      <div className="text-[15px] font-medium text-[#1E1E1E]">{b.name}</div>
                      {b.email && <div className="text-xs text-[#8A8A8A] mt-0.5">{b.email}</div>}
                      <div className="text-xs text-[#8A8A8A]">{b.phone}</div>
                    </div>
                    <div className="col-span-2 text-sm text-[#1E1E1E]/80">{b.service}</div>
                    <div className="col-span-2 text-sm text-[#1E1E1E]/80">{b.location}</div>
                    <div className="col-span-2 text-sm text-[#1E1E1E]/80">
                      <div>{b.created_at ? new Date(b.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                      <div className="text-xs text-[#8A8A8A]">{b.created_at ? new Date(b.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                    </div>
                    <div className="col-span-3">
                      <select
                        value={b.status}
                        onChange={(e) => updateStatus(b.id, e.target.value)}
                        data-testid={`status-select-${b.id}`}
                        className="text-xs uppercase tracking-[0.2em] border border-[#1E1E1E]/15 px-3 py-1.5 bg-white focus:border-[#A67C00] outline-none"
                      >
                        {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                      </select>
                      {b.email_status && (
                        <div className={`mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] ${b.email_status === "sent" ? "text-[#4A7C59]" : b.email_status === "failed" ? "text-[#B4453A]" : "text-[#8A8A8A]"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${b.email_status === "sent" ? "bg-[#4A7C59]" : b.email_status === "failed" ? "bg-[#B4453A]" : "bg-[#8A8A8A]"}`} />
                          {b.email_status === "sent" ? "Email sent" : b.email_status === "failed" ? "Email failed" : "Email not sent"}
                        </div>
                      )}
                      {b.concern && <div className="mt-2 text-xs text-[#1E1E1E]/60 max-w-xs">{b.concern}</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "contacts" && (
            <div className="bg-white border border-[#1E1E1E]/10" data-testid="contacts-table">
              <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#1E1E1E]/10 text-[10px] uppercase tracking-[0.24em] text-[#8A8A8A]">
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Contact</div>
                <div className="col-span-6">Message</div>
              </div>
              {loading ? (
                <div className="p-8 text-sm text-[#8A8A8A]">Loading…</div>
              ) : contacts.length === 0 ? (
                <div className="p-8 text-sm text-[#8A8A8A]">No inquiries yet.</div>
              ) : (
                contacts.map((c) => (
                  <div key={c.id} className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[#1E1E1E]/5" data-testid={`contact-row-${c.id}`}>
                    <div className="col-span-3 text-[15px]">{c.name}</div>
                    <div className="col-span-3 text-sm text-[#1E1E1E]/70">
                      <div>{c.email}</div>
                      <div className="text-xs">{c.phone}</div>
                    </div>
                    <div className="col-span-6 text-sm text-[#1E1E1E]/80">{c.message}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function SideBtn({ icon, label, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${active ? "border-[#A67C00] bg-[#F5F4EF] text-[#1E1E1E]" : "border-transparent text-[#1E1E1E]/70 hover:text-[#1E1E1E]"}`}>
      {icon}<span>{label}</span>
    </button>
  );
}

function StatCard({ label, value, gold }) {
  return (
    <div className="bg-white border border-[#1E1E1E]/10 p-6">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A8A8A]">{label}</div>
      <div className={`mt-4 font-display text-5xl tracking-tighter ${gold ? "text-[#A67C00]" : "text-[#1E1E1E]"}`}>{value ?? 0}</div>
    </div>
  );
}
